import clsx from 'clsx';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { useAuthContext } from '@/auth';
import { KeenIcon } from '@/components';
import { useLayout } from '@/providers';
import { useUser } from '@/contexts/UserContext.tsx';

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirmation: ''
};

const signupSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, 'Mindestens 2 Zeichen')
    .max(50, 'Maximal 50 Zeichen')
    .required('Vorname ist erforderlich'),
  lastName: Yup.string()
    .min(2, 'Mindestens 2 Zeichen')
    .max(50, 'Maximal 50 Zeichen')
    .required('Nachname ist erforderlich'),
  email: Yup.string()
    .email('Ungültiges E-Mail-Format')
    .min(3, 'Mindestens 3 Zeichen')
    .max(50, 'Maximal 50 Zeichen')
    .required('E-Mail ist erforderlich'),
  password: Yup.string()
    .min(8, 'Mindestens 8 Zeichen')
    .max(50, 'Maximal 50 Zeichen')
    .required('Passwort ist erforderlich'),
  passwordConfirmation: Yup.string()
    .min(8, 'Mindestens 8 Zeichen')
    .max(50, 'Maximal 50 Zeichen')
    .required('Passwortbestätigung ist erforderlich')
    .oneOf([Yup.ref('password')], 'Passwort und Bestätigung stimmen nicht überein')
});

const Signup = () => {
  const [loading, setLoading] = useState(false);
  const { register } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { currentLayout } = useLayout();
  const { refreshUser } = useUser();

  const formik = useFormik({
    initialValues,
    validationSchema: signupSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        if (!register) {
          throw new Error('JWTProvider ist für dieses Formular erforderlich.');
        }

        const success = await register(
          values.email,
          values.password,
          values.passwordConfirmation,
          values.firstName,
          values.lastName
        );

        if (success) {
          navigate(from, { replace: true });
          await refreshUser();
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Ein unerwarteter Fehler ist aufgetreten.';
        console.error(errorMessage);
        setStatus(errorMessage);
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    }
  });

  const togglePassword = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setShowPassword(!showPassword);
  };

  const toggleConfirmPassword = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="card max-w-[370px] w-full">
      <form
        className="card-body flex flex-col gap-5 p-10"
        noValidate
        onSubmit={formik.handleSubmit}
      >
        <div className="text-center mb-2.5">
          <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">Registrieren</h3>
          <div className="flex items-center justify-center font-medium">
            <span className="text-2sm text-gray-600 me-1.5">Bereits ein Konto?</span>
            <Link
              to={currentLayout?.name === 'auth-branded' ? '/auth/login' : '/auth/classic/login'}
              className="text-2sm link"
            >
              Anmelden
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900">Vorname</label>
          <label className="input">
            <input
              type="text"
              placeholder="Vorname eingeben"
              autoComplete="off"
              {...formik.getFieldProps('firstName')}
              className={clsx('form-control bg-transparent', {
                'is-invalid': formik.touched.firstName && formik.errors.firstName,
                'is-valid': formik.touched.firstName && !formik.errors.firstName
              })}
            />
          </label>
          {formik.touched.firstName && formik.errors.firstName && (
            <span role="alert" className="text-danger text-xs mt-1">
              {formik.errors.firstName}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900">Nachname</label>
          <label className="input">
            <input
              type="text"
              placeholder="Nachname eingeben"
              autoComplete="off"
              {...formik.getFieldProps('lastName')}
              className={clsx('form-control bg-transparent', {
                'is-invalid': formik.touched.lastName && formik.errors.lastName,
                'is-valid': formik.touched.lastName && !formik.errors.lastName
              })}
            />
          </label>
          {formik.touched.lastName && formik.errors.lastName && (
            <span role="alert" className="text-danger text-xs mt-1">
              {formik.errors.lastName}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900">E-Mail</label>
          <label className="input">
            <input
              placeholder="email@beispiel.de"
              type="email"
              autoComplete="off"
              {...formik.getFieldProps('email')}
              className={clsx('form-control bg-transparent', {
                'is-invalid': formik.touched.email && formik.errors.email,
                'is-valid': formik.touched.email && !formik.errors.email
              })}
            />
          </label>
          {formik.touched.email && formik.errors.email && (
            <span role="alert" className="text-danger text-xs mt-1">
              {formik.errors.email}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900">Passwort</label>
          <label className="input">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Passwort eingeben"
              autoComplete="off"
              {...formik.getFieldProps('password')}
              className={clsx('form-control bg-transparent', {
                'is-invalid': formik.touched.password && formik.errors.password,
                'is-valid': formik.touched.password && !formik.errors.password
              })}
            />
            <button className="btn btn-icon" onClick={togglePassword}>
              <KeenIcon icon="eye" className={clsx('text-gray-500', { hidden: showPassword })} />
              <KeenIcon
                icon="eye-slash"
                className={clsx('text-gray-500', { hidden: !showPassword })}
              />
            </button>
          </label>
          {formik.touched.password && formik.errors.password && (
            <span role="alert" className="text-danger text-xs mt-1">
              {formik.errors.password}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900">Passwort bestätigen</label>
          <label className="input">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Passwort erneut eingeben"
              autoComplete="off"
              {...formik.getFieldProps('passwordConfirmation')}
              className={clsx('form-control bg-transparent', {
                'is-invalid':
                  formik.touched.passwordConfirmation && formik.errors.passwordConfirmation,
                'is-valid':
                  formik.touched.passwordConfirmation && !formik.errors.passwordConfirmation
              })}
            />
            <button className="btn btn-icon" onClick={toggleConfirmPassword}>
              <KeenIcon
                icon="eye"
                className={clsx('text-gray-500', { hidden: showConfirmPassword })}
              />
              <KeenIcon
                icon="eye-slash"
                className={clsx('text-gray-500', { hidden: !showConfirmPassword })}
              />
            </button>
          </label>
          {formik.touched.passwordConfirmation && formik.errors.passwordConfirmation && (
            <span role="alert" className="text-danger text-xs mt-1">
              {formik.errors.passwordConfirmation}
            </span>
          )}
        </div>
        <button
          type="submit"
          className="btn btn-primary flex justify-center grow"
          disabled={loading || formik.isSubmitting}
        >
          {loading ? 'Bitte warten …' : 'Registrieren'}
        </button>

        {formik.status && (
          <div className="text-danger text-xs mt-1" role="alert">
            {formik.status}
          </div>
        )}
      </form>
    </div>
  );
};

export { Signup };
